import type { PackageEditErrors, PackageEditField, PackageEditForm } from './packageEditModel';
import { DESCRIPTION_MAX, NAME_MAX } from './packageEditModel';

type TextKey = 'nameEn' | 'nameAr' | 'descriptionEn' | 'descriptionAr';
type DateKey = 'startsAt' | 'endsAt';

export interface PackageEditDetailsProps {
  form: PackageEditForm;
  errors: PackageEditErrors;
  disabled: boolean;
  startLocked: boolean;
  minEnd: string;
  onChange: (key: TextKey | DateKey, value: string, clear?: PackageEditField[]) => void;
}

const errId = (f: PackageEditField) => `package-edit-err-${f}`;

/** Spec 081b admin edit — names, descriptions (EN/AR, RTL for Arabic) and the optional window. */
export default function PackageEditDetails(props: PackageEditDetailsProps) {
  const { form, errors, disabled, startLocked, minEnd, onChange } = props;

  const err = (f: PackageEditField) => (errors[f]
    ? <div className="offer-edit__error" id={errId(f)} role="alert">{errors[f]}</div>
    : null);
  const aria = (f: PackageEditField) => (errors[f] ? { 'aria-invalid': true as const, 'aria-describedby': errId(f) } : {});

  const text = (key: TextKey, label: string, max: number, rtl: boolean, multiline: boolean) => {
    const common = {
      id: `package-edit-${key}`,
      dir: rtl ? 'rtl' : 'ltr',
      lang: rtl ? 'ar' : undefined,
      maxLength: max,
      value: form[key],
      disabled,
      ...aria(key),
    };
    return (
      <div className="admin-form-group">
        <label className="admin-form-label" htmlFor={common.id}>{label}</label>
        {multiline
          ? <textarea className="admin-textarea" rows={4} {...common} onChange={(e) => onChange(key, e.target.value)} />
          : <input className="admin-form-input" type="text" {...common} onChange={(e) => onChange(key, e.target.value)} />}
        <div className="offer-edit__counter">{form[key].length}/{max}</div>
        {err(key)}
      </div>
    );
  };

  return (
    <section className="admin-card" aria-labelledby="package-edit-details">
      <div className="admin-card-header"><h3 className="admin-card-title" id="package-edit-details">Details</h3></div>
      <div className="offer-edit__cols">
        {text('nameEn', 'Name (English)', NAME_MAX, false, false)}
        {text('nameAr', 'Name (Arabic, optional)', NAME_MAX, true, false)}
        {text('descriptionEn', 'Description (English)', DESCRIPTION_MAX, false, true)}
        {text('descriptionAr', 'Description (Arabic, optional)', DESCRIPTION_MAX, true, true)}
        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="package-edit-startsAt">Starts on (optional)</label>
          <input id="package-edit-startsAt" className="admin-form-input" type="date" value={form.startsAt}
            disabled={disabled || startLocked} {...aria('startsAt')}
            onChange={(e) => onChange('startsAt', e.target.value, ['startsAt', 'endsAt'])} />
          {startLocked && <p className="offer-edit__hint">A running package keeps its start date.</p>}
          {err('startsAt')}
        </div>
        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="package-edit-endsAt">Ends on (optional)</label>
          <input id="package-edit-endsAt" className="admin-form-input" type="date" value={form.endsAt} min={minEnd}
            disabled={disabled} {...aria('endsAt')}
            onChange={(e) => onChange('endsAt', e.target.value)} />
          <p className="offer-edit__hint">Leave empty for no end date. Up to 90 days when both dates are set.</p>
          {err('endsAt')}
        </div>
      </div>
    </section>
  );
}
